import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Fix Leaflet icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Category { id: number; name_ar: string; }
interface Service { id: number; name_ar: string; }

const LocationPicker: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const SellerProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // أمان: نقبل فقط مسارًا داخليًا يبدأ بـ / وليس // (يمنع التوجيه لموقع خارجي — open redirect)
  const rawRedirect = params.get('redirect') || '/ads/create';
  const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')
    ? rawRedirect
    : '/ads/create';
  const { user, isAuthenticated, isLoading: authLoading, updateUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [accountType, setAccountType] = useState<string>('individual');
  const [isSeller, setIsSeller] = useState<boolean>(false);
  const [targetType, setTargetType] = useState<'individual' | 'company'>('individual');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedServices, setSelectedServices] = useState<number[]>([]);

  const [companyName, setCompanyName] = useState('');
  const [legalRep, setLegalRep] = useState('');

  useEffect(() => {
    // التوكن صار في كوكي HttpOnly (غير مقروء من JS) — نعتمد على حالة المصادقة
    if (!authLoading && !isAuthenticated) {
      toast.error('يجب تسجيل الدخول أولاً');
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, srvRes, profRes] = await Promise.all([
          api.get('/categories'),
          api.get('/services'),
          api.get('/profile/seller-profile'),
        ]);
        setCategories(catRes.data.data || []);
        setServices(srvRes.data.data || []);

        const p = profRes.data.data || {};
        setAccountType(p.account_type || 'individual');
        setIsSeller(!!p.is_seller);
        // القيمة الابتدائية للهدف: بائع شركة إن كان كذلك، وإلا بائع فرد
        setTargetType(p.is_seller && p.account_type === 'company' ? 'company' : 'individual');
        if (p.latitude != null && p.longitude != null) {
          setLatitude(Number(p.latitude));
          setLongitude(Number(p.longitude));
        }
        setSelectedCategories(p.category_ids || []);
        setSelectedServices(p.service_ids || []);
        setCompanyName(p.company_name || '');
        setLegalRep(p.legal_representative || '');
      } catch (err) {
        // ignore — الصفحة تعمل حتى لو تعذّر التحميل المسبق
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleCategory = (id: number) => {
    setSelectedCategories((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };
  const toggleService = (id: number) => {
    setSelectedServices((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('المتصفح لا يدعم تحديد الموقع'); return; }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
        setGettingLocation(false);
        toast.success('تم تحديد موقعك');
      },
      () => {
        setGettingLocation(false);
        toast.error('فشل تحديد الموقع، حدد يدوياً على الخريطة');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (latitude == null || longitude == null) { toast.error('حدد موقعك على الخريطة'); return; }
    if (selectedCategories.length === 0 && selectedServices.length === 0) {
      toast.error('اختر نوعاً واحداً على الأقل مما تقدّمه'); return;
    }
    if (targetType === 'company') {
      if (!companyName.trim()) { toast.error('أدخل اسم الشركة'); return; }
      if (!legalRep.trim()) { toast.error('أدخل المفوّض النظامي'); return; }
    }

    try {
      setSaving(true);
      await api.put('/profile/seller-profile', {
        target_type: targetType,
        latitude,
        longitude,
        category_ids: selectedCategories,
        service_ids: selectedServices,
        ...(targetType === 'company' ? { company_name: companyName, legal_representative: legalRep } : {}),
      });
      // تحديث الحساب محلياً ليصبح بائعاً فوراً (تظهر روابط البائع)
      if (user) {
        updateUser({ ...user, is_seller: true, account_type: targetType } as any);
      }
      toast.success('تم حفظ بيانات نشاطك');
      navigate(redirectTo);
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        toast.error(Array.isArray(first) ? (first[0] as string) : String(first));
      } else {
        toast.error(err?.response?.data?.message || 'حدث خطأ');
      }
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || !isAuthenticated) return null;

  if (loading) {
    return (
      <div className="max-w-site mx-auto px-4 py-16 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-1">{isSeller ? 'بيانات نشاطك' : 'كن بائعاً'}</h1>
      <p className="text-sm text-gray-500 mb-6">أكمل بياناتك حتى تتمكن من نشر إعلاناتك</p>

      <div className="space-y-6">
        {/* نوع حساب البائع */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-1">نوع حساب البائع *</h2>
          <p className="text-xs text-gray-400 mb-3">
            {isSeller ? 'يمكنك الترقية فقط — لا يمكن التراجع' : 'اختر كيف تريد البيع'}
          </p>
          <div className="flex gap-3">
            <button type="button"
              disabled={isSeller && accountType === 'company'}
              onClick={() => setTargetType('individual')}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold border-2 transition ${targetType === 'individual' ? 'border-primary-500 bg-primary-50 text-primary-500' : 'border-gray-200 text-gray-500'} ${isSeller && accountType === 'company' ? 'opacity-40 cursor-not-allowed' : ''}`}>
              بائع فرد
            </button>
            <button type="button" onClick={() => setTargetType('company')}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold border-2 transition ${targetType === 'company' ? 'border-primary-500 bg-primary-50 text-primary-500' : 'border-gray-200 text-gray-500'}`}>
              بائع شركة
            </button>
          </div>
          {isSeller && accountType === 'company' && (
            <p className="text-[11px] text-gray-400 mt-2">حسابك حالياً «بائع شركة» — أعلى فئة.</p>
          )}
        </div>

        {/* الموقع */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-3">موقعك *</h2>
          <button type="button" onClick={getCurrentLocation} disabled={gettingLocation}
            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-primary-500 text-primary-500 rounded-lg text-sm font-semibold hover:bg-primary-50 transition disabled:opacity-50 mb-3">
            {gettingLocation ? <span>جاري تحديد الموقع...</span> : <span>📍 استخدم موقعي الحالي</span>}
          </button>
          <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: '260px' }}>
            <MapContainer
              center={latitude && longitude ? [latitude, longitude] : [24.7136, 46.6753]}
              zoom={latitude ? 13 : 6}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
            >
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <LocationPicker onLocationSelect={(lat, lng) => { setLatitude(lat); setLongitude(lng); }} />
              {latitude && longitude && <Marker position={[latitude, longitude]} />}
            </MapContainer>
          </div>
          {latitude && longitude && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center mt-2">
              <p className="text-[11px] text-green-600 font-semibold" dir="ltr">✓ {latitude.toFixed(4)}, {longitude.toFixed(4)}</p>
            </div>
          )}
        </div>

        {/* ما يقدّمه */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-700 mb-1">الأنواع التي تقدّمها *</h2>
          <p className="text-xs text-gray-400 mb-4">اختر ما تتعامل به (تصنيف أو خدمة واحدة على الأقل)</p>

          {categories.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">التصنيفات</label>
              <div className="grid grid-cols-3 gap-2">
                {categories.map((cat) => (
                  <button key={cat.id} type="button" onClick={() => toggleCategory(cat.id)}
                    className={`p-2.5 rounded-lg border-2 text-sm font-medium transition ${selectedCategories.includes(cat.id) ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    {cat.name_ar}
                  </button>
                ))}
              </div>
            </div>
          )}

          {services.length > 0 && (
            <div>
              <label className="block text-sm text-gray-600 mb-2">الخدمات</label>
              <div className="grid grid-cols-2 gap-2">
                {services.map((srv) => (
                  <button key={srv.id} type="button" onClick={() => toggleService(srv.id)}
                    className={`p-2.5 rounded-lg border-2 text-sm font-medium transition ${selectedServices.includes(srv.id) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    {srv.name_ar}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* بيانات الشركة — عند اختيار بائع شركة */}
        {targetType === 'company' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="font-bold text-gray-700 mb-4">بيانات الشركة</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">اسم الشركة / المؤسسة *</label>
                <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="اسم الشركة أو المؤسسة"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">المفوّض النظامي *</label>
                <input type="text" value={legalRep} onChange={(e) => setLegalRep(e.target.value)} placeholder="اسم المفوّض النظامي"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex-1 bg-primary-500 text-white py-3.5 rounded-xl font-semibold hover:bg-primary-600 transition disabled:opacity-50 text-lg">
            {saving ? 'جاري الحفظ...' : 'حفظ ومتابعة'}
          </button>
          <button type="button" onClick={() => navigate(-1)}
            className="px-8 bg-gray-100 text-gray-600 py-3.5 rounded-xl font-semibold hover:bg-gray-200 transition">
            رجوع
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellerProfilePage;
